import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from 'convex/react'
import { useAuthActions } from '@convex-dev/auth/react'
import { Check, Copy, Download } from 'lucide-react'
import { api } from '../../lib/api'
import type { Doc } from '../../lib/api'
import { publicPageUrl, PUBLIC_ORIGIN } from '../../lib/config'
import { getDnsInstructions } from '../../lib/customDomains'
import { Spinner } from '../ui/Spinner'

const PUBLIC_HOST = PUBLIC_ORIGIN.replace(/^https?:\/\//, '')
type Sub = 'account' | 'billing' | 'subscribers'

const card = 'rounded-xl border border-zinc-800 bg-zinc-900 p-5'
const input =
  'w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-sm text-white outline-none focus:border-fuchsia-500'

export function SettingsTab({ page }: { page: Doc<'pages'> }) {
  const [sub, setSub] = useState<Sub>('account')
  return (
    <div>
      <div className="mb-6 flex justify-center gap-2">
        {(['account', 'billing', 'subscribers'] as Sub[]).map((s) => (
          <button
            key={s}
            onClick={() => setSub(s)}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold capitalize ${
              sub === s ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      {sub === 'account' && <Account page={page} />}
      {sub === 'billing' && <Billing />}
      {sub === 'subscribers' && <Subscribers />}
    </div>
  )
}

function Account({ page }: { page: Doc<'pages'> }) {
  const claimPage = useMutation(api.pages.claimPage)
  const deleteAccount = useMutation(api.account.deleteMyAccount)
  const { signOut } = useAuthActions()
  const navigate = useNavigate()

  const [username, setUsername] = useState(page.usernameDisplay || page.username)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  async function rename() {
    setErr(null)
    setMsg(null)
    setBusy(true)
    try {
      await claimPage({ username })
      setMsg('Username updated.')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not update username')
    } finally {
      setBusy(false)
    }
  }

  async function copyLink() {
    await navigator.clipboard.writeText(publicPageUrl(page.username))
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  async function doDelete() {
    setBusy(true)
    try {
      await deleteAccount({})
      await signOut()
      navigate('/')
    } catch {
      setErr('Could not delete account')
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-lg space-y-5">
      <div className={card}>
        <h3 className="text-base font-bold text-white">Username</h3>
        {page.status === 'published' && (
          <p className="mt-1 text-xs text-amber-400">
            Your page is live. Renaming changes your public URL and breaks existing links.
          </p>
        )}
        <div className="mt-3 flex gap-2">
          <input value={username} onChange={(e) => setUsername(e.target.value)} className={input} />
          <button
            onClick={rename}
            disabled={busy}
            className="rounded-lg bg-fuchsia-500 px-4 py-2 text-sm font-bold text-white hover:bg-fuchsia-400 disabled:opacity-50"
          >
            Update
          </button>
        </div>
        {msg && <p className="mt-2 text-sm text-emerald-400">{msg}</p>}
        {err && <p className="mt-2 text-sm text-rose-400">{err}</p>}
      </div>

      <div className={card}>
        <h3 className="text-base font-bold text-white">Your page link</h3>
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5">
          <span className="flex-1 truncate text-sm text-zinc-300">
            {PUBLIC_HOST}/{page.username}
          </span>
          <button onClick={copyLink} className="text-zinc-400 hover:text-white" aria-label="Copy">
            {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <CustomDomain />

      <div className={card}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-white">Danger zone</h3>
          <button
            onClick={() => signOut().then(() => navigate('/'))}
            className="rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:text-white"
          >
            Log out
          </button>
        </div>
        {!confirmingDelete ? (
          <button
            onClick={() => setConfirmingDelete(true)}
            className="mt-3 text-sm text-rose-400 hover:text-rose-300"
          >
            Delete my account
          </button>
        ) : (
          <div className="mt-3 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3">
            <p className="text-sm text-rose-200">
              This permanently deletes your page, startups, and subscribers. This cannot be undone.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={doDelete}
                disabled={busy}
                className="rounded-lg bg-rose-500 px-4 py-2 text-sm font-bold text-white hover:bg-rose-400 disabled:opacity-50"
              >
                {busy ? <Spinner className="h-4 w-4" /> : 'Yes, delete everything'}
              </button>
              <button
                onClick={() => setConfirmingDelete(false)}
                className="rounded-lg px-4 py-2 text-sm text-zinc-400 hover:text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function CustomDomain() {
  const domain = useQuery(api.domains.getMyDomain, {})
  const setDomain = useMutation(api.domains.setCustomDomain)
  const removeDomain = useMutation(api.domains.removeCustomDomain)
  const requestVerify = useMutation(api.domains.requestVerification)
  const retryProvisioning = useMutation(api.domains.retryProvisioning)

  const [value, setValue] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [checking, setChecking] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [copied, setCopied] = useState(false)

  async function save() {
    setErr(null)
    setBusy(true)
    try {
      await setDomain({ domain: value })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save domain')
    } finally {
      setBusy(false)
    }
  }

  async function verify() {
    setErr(null)
    setChecking(true)
    try {
      await requestVerify({})
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not verify DNS')
    } finally {
      setChecking(false)
    }
  }

  async function retry() {
    setErr(null)
    try {
      if (domain?.provisioningStatus === 'failed') {
        await retryProvisioning({})
      } else {
        await verify()
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not retry domain setup')
    }
  }

  async function copyDomainLink() {
    if (!domain?.domain) return
    await navigator.clipboard.writeText(`https://${domain.domain}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  async function remove() {
    if (!domain?.domain) return
    const confirmed = window.confirm(
      `Remove ${domain.domain}? It will stop serving your MakerStack page immediately.`,
    )
    if (!confirmed) return
    setErr(null)
    setRemoving(true)
    try {
      await removeDomain({})
      setValue('')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not remove domain')
    } finally {
      setRemoving(false)
    }
  }

  const instructions =
    domain?.domain && domain.token ? getDnsInstructions(domain.domain, domain.token) : null
  const isVerifying = checking || domain?.status === 'verifying'
  const status =
    busy
      ? 'Saving'
      : domain?.provisioningStatus === 'pending'
        ? 'Registering domain with Netlify'
        : domain?.provisioningStatus === 'failed' || domain?.status === 'failed'
          ? 'Failed'
          : domain?.status === 'verifying'
            ? 'Verifying ownership'
            : domain?.status === 'provisioning_https'
              ? 'Provisioning HTTPS'
              : domain?.status === 'live'
                ? 'Live'
                : 'Waiting for DNS records'
  const statusTone =
    status === 'Live'
      ? 'bg-emerald-500/15 text-emerald-400'
      : status === 'Failed'
        ? 'bg-rose-500/15 text-rose-400'
        : 'bg-amber-500/15 text-amber-400'

  return (
    <div className={card}>
      <h3 className="text-base font-bold text-white">Custom domain</h3>
      {!domain?.domain ? (
        <>
          <p className="mt-1 text-sm text-zinc-400">Serve your page from your own domain.</p>
          <div className="mt-3 flex gap-2">
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="yourname.com"
              className={input}
            />
            <button
              onClick={save}
              disabled={busy || !value.trim()}
              className="rounded-lg bg-fuchsia-500 px-4 py-2 text-sm font-bold text-white hover:bg-fuchsia-400 disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
          {err && <p className="mt-2 text-sm text-rose-400">{err}</p>}
        </>
      ) : (
        <div className="mt-2 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-200">{domain.domain}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusTone}`}>
              {status}
            </span>
          </div>
          {domain.status === 'live' ? (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
              <p className="text-sm text-emerald-200">
                Your page is live at{' '}
                <a
                  href={`https://${domain.domain}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold underline hover:text-white"
                >
                  https://{domain.domain}
                </a>
              </p>
              <button
                onClick={copyDomainLink}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-300 hover:text-white"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
          ) : instructions && domain.provisioningStatus === 'provisioned' ? (
            <div className="rounded-lg border border-zinc-700 bg-zinc-950 p-3 text-xs text-zinc-400">
              <p className="mb-2 font-semibold text-zinc-300">Add these DNS records:</p>
              <p className="font-mono text-zinc-200">
                Type: {instructions.verificationRecord.type}
                <br />
                Host/Name: {instructions.verificationRecord.host}
                <br />
                Value: {instructions.verificationRecord.value}
              </p>
              <p className="mt-3 font-mono text-zinc-200">
                Type: {instructions.routingRecord.type}
                <br />
                Host/Name: {instructions.routingRecord.host}
                <br />
                Value: {instructions.routingRecord.value}
              </p>
              <p className="mt-2">
                {instructions.routingHelp} Preserve unrelated MX, DKIM, SPF, DMARC, and other
                subdomain records.
              </p>
            </div>
          ) : null}
          {(domain.provisioningError || domain.statusError || err) && (
            <p className="text-sm text-rose-400">
              {err || domain.provisioningError || domain.statusError}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {!domain.verified && domain.provisioningStatus === 'provisioned' && (
              <button
                onClick={verify}
                disabled={isVerifying}
                className="rounded-lg bg-fuchsia-500 px-4 py-2 text-sm font-bold text-white hover:bg-fuchsia-400 disabled:opacity-50"
              >
                {isVerifying ? 'Verifying ownership…' : 'Verify DNS'}
              </button>
            )}
            {(domain.provisioningStatus === 'failed' || domain.status === 'failed') && (
              <button
                onClick={retry}
                disabled={checking}
                className="rounded-lg bg-fuchsia-500 px-4 py-2 text-sm font-bold text-white hover:bg-fuchsia-400 disabled:opacity-50"
              >
                Retry
              </button>
            )}
            <button
              onClick={remove}
              disabled={removing}
              className="rounded-lg border border-zinc-700 px-4 py-2 text-sm text-zinc-300 hover:text-white"
            >
              {removing ? 'Removing…' : 'Remove domain'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function Billing() {
  const ent = useQuery(api.billing.getMyEntitlement, {})
  const label =
    ent?.tier === 'lifetime'
      ? 'Lifetime Deal'
      : ent?.tier === 'year_pass'
        ? '1-Year Pass'
        : 'Free (not published)'

  return (
    <div className="mx-auto max-w-lg space-y-5">
      <div className={card}>
        <h3 className="text-base font-bold text-white">Your plan</h3>
        <p className="mt-2">
          <span className="rounded-full bg-zinc-800 px-3 py-1 text-sm text-zinc-200">{label}</span>
        </p>
        {ent?.tier === 'year_pass' && ent.expiresAt && (
          <p className="mt-2 text-sm text-zinc-400">
            Renews/expires {new Date(ent.expiresAt).toLocaleDateString()}
          </p>
        )}
        {ent?.tier === 'none' && (
          <a
            href="/pricing"
            className="mt-4 inline-block rounded-lg bg-fuchsia-500 px-4 py-2 text-sm font-bold text-white hover:bg-fuchsia-400"
          >
            See pricing
          </a>
        )}
        {ent?.tier !== 'none' && (
          <p className="mt-4 text-sm text-zinc-500">
            Manage payment details and invoices from the email receipt sent by our payment partner.
          </p>
        )}
      </div>
    </div>
  )
}

function Subscribers() {
  const subs = useQuery(api.subscribers.listMySubscribers, {})
  const count = useQuery(api.subscribers.mySubscriberCount, {})

  function exportCsv() {
    if (!subs?.length) return
    const rows = [
      ['email', 'status', 'subscribed_at'],
      ...subs.map((s) => [s.email, s.status, new Date(s.createdAt).toISOString()]),
    ]
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url
    a.download = 'subscribers.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="mx-auto max-w-lg space-y-5">
      <div className={card}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-white">Subscribers</h3>
            <p className="mt-1 text-sm text-zinc-400">{count ?? 0} active</p>
          </div>
          <button
            onClick={exportCsv}
            disabled={!subs?.length}
            className="flex items-center gap-2 rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:text-white disabled:opacity-40"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
        {subs === undefined ? (
          <div className="mt-4 flex justify-center">
            <Spinner />
          </div>
        ) : subs.length === 0 ? (
          <p className="mt-4 text-sm text-zinc-500">
            No subscribers yet. Enable email collection in the Page tab.
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-zinc-800">
            {subs.slice(0, 50).map((s) => (
              <li key={s.email} className="flex items-center justify-between py-2 text-sm">
                <span className="text-zinc-200">{s.email}</span>
                <span className="text-xs text-zinc-500">
                  {new Date(s.createdAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
